import { Component, Input, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-document-viewer',
    templateUrl: './document-viewer.component.html',
    styleUrls: ['./document-viewer.component.css'],
    standalone: false
})
export class DocumentViewerComponent implements OnDestroy {
  @Input() downloadUrl = '';
  @Input() label = 'Ver documento';
  @Input() codigoObra = '';

  @Input() loginEndpoint = environment.ApiERO + '/auth/login';
  @Input() checklistEndpoint = environment.ApiERO + '/api/checklist';

  @Input() modulo = environment.moduloERO;


  @ViewChild('contenidoModal') contenidoModal!: TemplateRef<any>;
  dialogRef!: MatDialogRef<any>;

  loadingDoc = false;
  errorDoc: string | null = null;

  // Visor canvas espera { data: Uint8Array } o { url: string } / Uint8Array
  pdfSrc: any = null;

  private downloadedBlob: Blob | null = null;
  fileName = 'documento.pdf';

  // ====== Token cache (solo runtime, se pierde al recargar) ======
  private cachedToken: string | null = null;
  private cachedTokenExpMs: number | null = null;

  constructor(
    private dialog: MatDialog,
    private scrollStrategyOptions: ScrollStrategyOptions,
    private http: HttpClient
  ) { }
  page = 1;
  totalPages = 0;

  onPdfLoaded(pdf: any) {
    this.totalPages = pdf?.numPages || 0;
    this.page = 1; // siempre arranca en 1 al cargar
  }

  prevPage() {
    if (this.page > 1) this.page--;
  }

  nextPage() {
    if (this.page < this.totalPages) this.page++;
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  open() {
    this.loadingDoc = true;
    this.errorDoc = null;

    this.dialogRef = this.dialog.open(this.contenidoModal, {
      maxWidth: '100%',
      disableClose: true,
      panelClass: 'document-viewer-dialog',
      scrollStrategy: this.scrollStrategyOptions.noop(),
    });

    // Si no viene downloadUrl, primero consultamos a checklist (base64 o link)
    if (!this.downloadUrl?.trim()) {
      this.fetchDocumentThenPrepare();
      return;
    }

    // ✅ Mantiene la opción actual: visualizar por link (descarga)
    this.downloadAndPrepare();
  }

  close() {
    this.dialogRef?.close();
    this.cleanup();
  }

  /**
   * ✅ NUEVO:
   * - Pide al endpoint /api/checklist el documento.
   * - Intenta usar BASE64 si viene en la respuesta (return_base64:true).
   * - Si NO viene base64, cae al flujo anterior usando download_url.
   *
   * IMPORTANTE:
   * - NO borra el modo "download_url". Se mantiene tal cual.
   */
  private async fetchDocumentThenPrepare() {
    const codigo = (this.codigoObra || '').trim();
    // const codigo = '10189340'; // (solo para pruebas)

    if (!codigo) {
      this.errorDoc = 'No se recibió codigo_obra para consultar el PDF.';
      this.loadingDoc = false;
      return;
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-modulo': this.modulo,
      };


      let user = localStorage.getItem('user');
      const userObj = JSON.parse(user);

      // NO-PROD: solo API key (como ahora)
      if (!environment.production) {
        if (userObj.APIKEYERO) headers['x-api-key'] = userObj.APIKEYERO;
      } else {
        // PROD: token + (opcional) api key
        const token = await this.getTokenFetch();

        // Si en prod también piden api-key, deja esto.
        // Si NO la piden, bórralo.


        // headers['x-api-key'] = userObj.APIKEYERO;
        // Según lo que venías usando:
        headers['Authorization'] = `Bearer ${token}`;
        // Si tu doc fuera x-token, sería:
        // headers['x-token'] = token;
      }

      const res = await fetch(this.checklistEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          codigo_obra: codigo,
          return_base64: true, // ✅ lo pedimos, pero no quitamos el fallback a link
        }),
      });

      if (!res.ok) {
        throw new Error(`Checklist HTTP ${res.status}`);
      }

      const resp = await res.json();

      // ✅ Tu respuesta real confirma que existe resp.pdf_base64 y content_type
      const base64: string | undefined = resp?.pdf_base64;
      const contentType: string = resp?.content_type || 'application/pdf';

      // 1) ✅ Si viene base64, renderiza SIN tocar S3 (evita CORS)
      if (resp?.success && base64) {
        this.prepareFromBase64(base64, contentType, resp);
        this.loadingDoc = false;
        return;
      }

      // 2) ✅ Si no vino base64, mantenemos el modo antiguo por download_url
      const url = resp?.download_url;
      if (!resp?.success || !url) {
        this.errorDoc = 'La API no devolvió pdf_base64 ni download_url.';
        this.loadingDoc = false;
        return;
      }

      this.downloadUrl = url;
      this.downloadAndPrepare(); // ✅ flujo original intacto
    } catch (err) {
      console.error('CHECKLIST/LOGIN ERROR', err);
      this.errorDoc = environment.production
        ? 'No se pudo obtener token o el documento.'
        : 'No se pudo obtener el documento.';
      this.loadingDoc = false;
    }
  }

  /**
   * ✅ NUEVO:
   * Prepara pdfSrc y downloadedBlob desde base64 (para visualizar + descargar).
   */
  private prepareFromBase64(base64: string, contentType: string, resp?: any) {
    this.cleanup();

    const name = resp?.file_name || resp?.filename || 'documento.pdf';
    this.fileName = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;

    const bytes = this.base64ToUint8Array(base64);

    // Visor (puedes dejarlo así)
    this.pdfSrc = { data: bytes };

    // ✅ Normaliza para evitar ArrayBuffer|SharedArrayBuffer en el tipo
    const bytesFixed = new Uint8Array(bytes);

    // Para descargar / abrir en otra pestaña
    this.downloadedBlob = new Blob([bytesFixed], { type: contentType || 'application/pdf' });
  }



  /**
   * Convierte base64 a Uint8Array
   * - Soporta base64 "puro" (como tu ejemplo: "JVBERi0xL...")
   * - Soporta dataURL ("data:application/pdf;base64,JVBERi0xL...")
   */
  private base64ToUint8Array(b64: string): Uint8Array {
    const clean = b64.includes(',') ? b64.split(',')[1] : b64;

    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  // ====== PROD: obtiene token (cacheado) ======
  private async getTokenFetch(): Promise<string> {
    const now = Date.now();

    if (this.cachedToken && this.cachedTokenExpMs && now < this.cachedTokenExpMs) {
      return this.cachedToken;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Si el login también exige api key, deja esto:

    let user = localStorage.getItem('user');
    const userObj = JSON.parse(user);

    headers['x-api-key'] = userObj.APIKEYERO;

    const res = await fetch(this.loginEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        username: userObj.USUARIOERO,
        password: userObj.PASSWORDERO,
      }),
    });

    if (!res.ok) {
      throw new Error(`Login HTTP ${res.status}`);
    }

    const resp = await res.json();

    const token =
      resp?.token ||
      resp?.access_token ||
      resp?.accessToken ||
      resp?.data?.token ||
      resp?.data?.access_token;

    if (!token || typeof token !== 'string') {
      throw new Error('Login no devolvió token en un campo reconocido.');
    }

    const ttlSeconds =
      (typeof resp?.expires_in === 'number' && resp.expires_in) ||
      (typeof resp?.ttl === 'number' && resp.ttl) ||
      600;

    this.cachedToken = token;
    this.cachedTokenExpMs = Date.now() + (ttlSeconds * 1000) - 5_000;

    return token;
  }

  /**
   * ✅ MODO ORIGINAL (NO BORRAR):
   * Descarga el PDF desde downloadUrl (S3 presigned) y lo prepara para el visor canvas.
   *
   * Nota: si ese S3 no tiene CORS, el browser bloqueará el blob.
   * Por eso ahora preferimos base64 cuando viene disponible.
   */
  private downloadAndPrepare() {
    this.cleanup();

    this.http.get(this.downloadUrl, { responseType: 'blob', observe: 'response' })
      .subscribe({
        next: async (resp: HttpResponse<Blob>) => {
          const blob = resp.body;

          if (!blob) {
            this.errorDoc = 'No se recibió contenido para visualizar.';
            this.loadingDoc = false;
            return;
          }

          this.downloadedBlob = blob;

          const isPdf = await this.sniffPdf(blob);
          if (!isPdf) {
            this.errorDoc = 'El archivo recibido no parece ser un PDF.';
            this.loadingDoc = false;
            return;
          }

          const cd = resp.headers.get('Content-Disposition') || '';
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
          const rawName = decodeURIComponent(match?.[1] || match?.[2] || '');
          this.fileName = rawName ? rawName : 'documento.pdf';
          if (!this.fileName.toLowerCase().endsWith('.pdf')) this.fileName += '.pdf';

          const buffer = await blob.arrayBuffer();
          const uint8 = new Uint8Array(buffer);

          this.pdfSrc = { data: uint8 };
          this.loadingDoc = false;
        },
        error: (err) => {
          console.error('DOWNLOAD ERROR', err);
          this.errorDoc = 'No se pudo descargar el archivo para previsualizar.';
          this.loadingDoc = false;
        }
      });
  }

  private async sniffPdf(blob: Blob): Promise<boolean> {
    try {
      const head = await blob.slice(0, 4).arrayBuffer();
      const b = new Uint8Array(head);
      return b.length === 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46;
    } catch {
      return false;
    }
  }

  download() {
    if (!this.downloadedBlob) return;

    const blob = this.downloadedBlob.type === 'application/pdf'
      ? this.downloadedBlob
      : new Blob([this.downloadedBlob], { type: 'application/pdf' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName || 'documento.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  openInNewTab() {
    // Si ya tenemos blob (base64 o descarga), lo abrimos localmente
    if (this.downloadedBlob) {
      const blob = this.downloadedBlob.type === 'application/pdf'
        ? this.downloadedBlob
        : new Blob([this.downloadedBlob], { type: 'application/pdf' });

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }

    // Si no hay blob aún pero sí hay link, abrimos el link directo (fallback)
    if (this.downloadUrl) {
      window.open(this.downloadUrl, '_blank', 'noopener,noreferrer');
    }
  }

  private cleanup() {
    this.pdfSrc = null;
    this.downloadedBlob = null;
    this.fileName = 'documento.pdf';
    this.errorDoc = null;
  }
}


